import { SxProps, Box, Stack, Divider, Typography, Link, Popover, styled } from "@mui/material";
import React, { useEffect, useState } from "react";
import moment, { Moment } from "moment";
import { DateSelectionType } from "./DateSelectionType";
import { LocalDateRangePicker } from "./LocalDateRangePicker";
import { DropDownButton, DropDownButtonProps } from "../FilterDropdown/DropDownButton";
import { FormButton } from "../../atoms";
import { dateOnly } from "../../helper";

export const styles: SxProps = {
  zIndex: 1,
  p: "1.5625rem",
  bgcolor: "#030304",
  maxWidth: "300px",
};

export const ActionButton = styled(FormButton)({
  height: "2.5rem",
  textDecoration: "uppercases",
  borderRadius: "0.625rem",
});
const defaultTimes = ["Today", "3 days ago", "One week ago", "One Month ago", "Range"];
export type DateRangeFilterModel = {
  fromDate?: any;
  toDate?: any;
  type: DateSelectionType;
};
const initialState = { fromDate: undefined, toDate: undefined, type: DateSelectionType.None };
export interface DateRangeFilterProps {
  valueChanged?: (filter: DateRangeFilterModel) => void;
  display: DropDownButtonProps["display"];
  size?: DropDownButtonProps["size"];
}
export function DateRangeFilter({ valueChanged = () => {}, display = "Date", size = "small" }: DateRangeFilterProps) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [localDisplay, setLocalDisplay] = useState(display);
  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const [temp, setTemp] = React.useState<DateRangeFilterModel>(initialState);

  const presetClicked = (index: DateSelectionType) => {
    var from = moment(temp.fromDate);
    if (index === DateSelectionType.Today) {
      from = moment();
    } else if (index === DateSelectionType.ThreeDays) {
      from = moment().subtract(3, "days");
    } else if (index === DateSelectionType.OneWeek) {
      from = moment().subtract(7, "days");
    } else if (index === DateSelectionType.OneMonth) {
      from = moment().subtract(1, "months");
    } else if (index === DateSelectionType.Range) {
      from = moment().subtract(1, "weeks");
    }
    handleChange({ fromDate: from?.toISOString(), toDate: moment().toISOString(), type: index });
  };
  const handleChange = (params: any) => {
    setTemp((prev) => ({ ...prev, ...params }));
  };
  const handleReset = () => {
    setTemp((prev) => {
      valueChanged(initialState);
      updateDisplay(DateSelectionType.None);
      return initialState;
    });

    handleClose();
  };

  const updateDisplay = (type: DateSelectionType) => {
    if (type === DateSelectionType.None) {
      setLocalDisplay(display);
    } else if (type === DateSelectionType.Today) {
      setLocalDisplay("Today");
    } else if (type === DateSelectionType.ThreeDays) {
      setLocalDisplay("Three Days");
    } else if (type === DateSelectionType.OneWeek) {
      setLocalDisplay("One Week");
    } else if (type === DateSelectionType.OneMonth) {
      setLocalDisplay("One Month");
    } else {
      let d = `${moment(temp.fromDate).format("L")} - ${moment(temp.toDate).format("L")}`;
      setLocalDisplay(d);
    }
  };

  const handleApply = () => {
    valueChanged({ ...temp });
    updateDisplay(temp.type);
    handleClose();
  };
  return (
    <div>
      <DropDownButton size={size} display={localDisplay} handleClick={(e) => handleClick(e)} />
      <Popover
        sx={{ mt: 2 }}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            padding: 0,
          },
        }}
      >
        <Stack sx={styles} spacing={"1.25rem"}>
          <DefaultOptionView handleClick={presetClicked} type={temp.type} />
          <Divider />
          {temp.type === DateSelectionType.Range && <RangePickerView filter={temp} handleChange={handleChange} />}

          <ActionPanel type={temp.type} handleApply={handleApply} handleReset={handleReset} />
        </Stack>
      </Popover>
    </div>
  );
}
type RangePickerViewProps = {
  filter: any;
  handleChange: any;
};
const RangePickerView = ({ filter, handleChange }: RangePickerViewProps) => {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: "0.875rem",
          marginTop: "0.3125rem",
          marginBottom: "0.4375rem",
        }}
      >
        Custom
      </Typography>
      <Box>
        <LocalDateRangePicker filter={filter} onChange={handleChange} />
      </Box>
    </Box>
  );
};
const ActionPanel = ({ handleApply, handleReset, type }) => {
  return (
    <Box>
      <Stack direction="row" spacing="0.625rem" sx={{ marginTop: "1.25rem" }}>
        <ActionButton onClick={handleReset} color="primary" label="Reset" />
        <ActionButton disabled={type === DateSelectionType.None} label="Apply" onClick={handleApply} />
      </Stack>
    </Box>
  );
};

export default DateRangeFilter;
const DefaultOptionView = ({ handleClick, type }) => {
  return (
    <>
      {defaultTimes.map((item, index: number) => (
        <Link
          onClick={() => handleClick(index + 1)}
          href="#"
          key={index}
          sx={{
            textAlign: "left",
            color: index + 1 === type ? "success.main" : "secondary.main",
            textDecoration: "none",
            width: "100%",
            padding: 0,
            fontSize: "1rem",
            fontWeight: 500,
          }}
        >
          {item}
        </Link>
      ))}
    </>
  );
};
